<template>
  <div class="treeview">
    <slot
      :name="`tree.${identifier}.items`"
      :items="filteredItems"
    >
      <v-treeview
        ref="treeview"
        :activatable="!editable"
        :active.sync="activeItems"
        :item-children="itemChildren"
        :item-key="itemKey"
        :item-text="itemText"
        :items="filteredItems"
        :open.sync="openedItems"
        dense
        return-object
        selection-type="independent"
        transition
      >
        <v-sheet v-if="treeHasNoData(filteredItems)">
          <span class="grey--text">{{ $t('no-items-no-matches') }}</span>
        </v-sheet>

        <template v-slot:prepend="{ item, open }">
          <div
            :class="{'is-movable': isMovable(item)}"
            @dragleave="isMovable(item) ? onDragLeave({ $event }) : undefined"
            @dragover="isMovable(item) ? onDragOver({ $event }) : undefined"
            @drop="isMovable(item) ? onDrop({ $event, item }) : undefined"
            @mousedown="isMovable(item) ? onMouseDown({ $event, item }) : undefined"
          >
            <TreeviewIcon
              :active-item="item"
              :artefact="tree.artefact"
              :open="open"
            />
          </div>
        </template>

        <template v-slot:label="{ item, open }">
          <v-tooltip
            top
            :open-on-click="false"
            :open-on-focus="false"
            open-delay="1200"
            nudge-bottom="15"
            color="rgba(0,0,0, 0.9)"
            :disabled="isDragged(item) || !tooltipIds[getIndex(item)]"
          >
            <template #activator="{on}">
              <div class="d-flex" :data-activator="getIndex(item)">
                <div
                  class="dropable-layer flex-grow-1"
                  :class="{ 'dropable-layer-not-editable': !isMovable(item), 'is-movable': isMovable(item)}"
                  v-on="on"
                  @mousedown="isMovable(item) ? onMouseDown({ $event, item }) : undefined"
                >
                  <div
                    class="dropable-layer-before"
                    @dragleave="onDragLeave({ $event })"
                    @dragover="onDragOver({ $event, item, position: 'before' })"
                    @drop="onDrop({ $event, item, position: 'before' })"
                  />
                  <div
                    :class="{'dropable-layer-after': !open}"
                    @dragleave="onDragLeave({ $event })"
                    @dragover="onDragOver({ $event, item, position: 'after' })"
                    @drop="onDrop({ $event, item, position: 'after' })"
                  />
                  <div
                    class="dropable-layer-into"
                    v-if="isEmptyGroup({item})"
                    @dragleave="onDragLeave({ $event })"
                    @dragover="onDragOver({ $event, item, position: 'into' })"
                    @drop="onDrop({ $event, item, position: 'into' })"
                  />
                  <slot
                    :item="item"
                    :label="getTranslatedLabel(item, true)"
                    :name="`tree.${identifier}.label`"
                  >
                    {{ getTranslatedLabel(item, false) }}
                    <v-icon
                      v-if="item.property && item.property instanceof Array && item.property.some((property) => property.name === 'Keyword') && userIsProjectAuthor"
                      size="small"
                      class="ml-1"
                      :title="item.property.filter((property) => property.name === 'Keyword').map((property) => property['#text']).join(', ')"
                    >mdi-tag-outline
                    </v-icon>
                  </slot>
                </div>
                <div v-if="itemHasInsertOption({tree, item})" class="treeview-node-actions flex-grow-0">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        x-small
                        @click.stop.prevent="">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>

                    <v-list dense>
                      <template v-for="(insertPosition, key) of insertPositions">
                        <v-list-item
                          v-if="!!insertPosition.condition && insertPosition.condition(item)"
                          @click="onInsert({...insertPosition, item})"
                          :key="key"
                        >
                          <v-list-item-content>
                            <v-list-item-title>{{ $t(insertPosition.text) }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-list>

                  </v-menu>
                </div>
              </div>
            </template>
            {{ getTranslatedLabel(item, false) }}
          </v-tooltip>
        </template>
      </v-treeview>
    </slot>
  </div>
</template>

<script>

import TreeviewIcon from '@/components/Utilities/Treeview/TreeviewIcon'
import { isFunction } from 'lodash'

export default {
  name: 'AdTreeview',

  components: {
    TreeviewIcon
  },

  props: {
    activeItem: {
      type: Object,
      required: false,
      default: () => ({})
    },
    openId: {
      type: String,
      required: false,
      default: undefined
    },
    identifier: {
      type: String,
      required: false,
      default: ''
    },
    tree: {
      type: [Array, Object],
      required: false,
      default: () => []
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    filter: {
      type: Function,
      required: false,
      default: undefined
    },
    search: {
      type: String,
      required: false,
      default: ''
    },
    expand: {
      type: Boolean,
      required: false,
      default: false
    },
    category: {
      type: String,
      required: false,
      default: undefined
    },
    indicateNoData: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data () {
    return {
      tab: 0,
      isExpanded: false,
      openedId: null,
      openedIds: [],
      editableTree: [],
      activeItems: [],
      draggedItem: null,
      tooltipIds: {},
      timeoutHandler: null,
      filterKey: 'statusCode',
      insertPositions: [{
        type: 'group',
        mode: 'into',
        text: 'insert-new-group-into',
        condition: (item) => {
          // Check if artefact is scenario related.
          if (this.tree.artefact === 'SC') {
            // Only allow on root element.
            return !item?.type && !!item?.transaction
          }
          // Check if artefact is implementation guide related
          if (this.tree.artefact === 'IG') {
            return false
          }

          // Return default state of default behavior.
          return item?.type === 'group' && !!item?.inherit === false
        }
      }, {
        type: 'group',
        mode: 'preceding',
        text: 'insert-new-group-before',
        condition: (item) => {
          // Check if artefact is scenario related.
          if (this.tree.artefact === 'SC') {
            // Only allow on root element.
            return item?.type === 'group'
          }
          // Check if artefact is implementation guide related
          if (this.tree.artefact === 'IG') {
            return false
          }

          // Return default state of default behavior.
          return true
        }
      }, {
        type: 'group',
        mode: 'following',
        text: 'insert-new-group-after',
        condition: (item) => {
          // Check if artefact is scenario related.
          if (this.tree.artefact === 'SC') {
            // Only allow on root element.
            return item?.type === 'group'
          }
          // Check if artefact is implementation guide related
          if (this.tree.artefact === 'IG') {
            return false
          }

          // Return default state of default behavior.
          return true
        }
      }, {
        type: 'item',
        mode: 'into',
        text: 'insert-new-item-into',
        condition: (item) => (item?.type === 'group' && !!item?.inherit === false) || this.tree.artefact === 'IG'
      }, {
        type: 'item',
        mode: 'preceding',
        text: 'insert-new-item-before',
        condition: (item) => {
          // Check if artefact is scenario related.
          if (this.tree.artefact === 'SC') {
            // Only allow on root element.
            return !!item?.type && item?.type !== 'group'
          }
          // Check if artefact is implementation guide related
          if (this.tree.artefact === 'IG') {
            const rootId = this.tree?.items?.[0]?.id
            return !!rootId && rootId !== item.id
          }

          // Return default state of default behavior.
          return true
        }
      }, {
        type: 'item',
        mode: 'following',
        text: 'insert-new-item-after',
        condition: (item) => {
          // Check if artefact is scenario related.
          if (this.tree.artefact === 'SC') {
            // Only allow on root element.
            return !!item?.type && item?.type !== 'group'
          }
          // Check if artefact is implementation guide related
          if (this.tree.artefact === 'IG') {
            const rootId = this.tree?.items?.[0]?.id
            return !!rootId && rootId !== item.id
          }

          // Return default state of default behavior.
          return true
        }
      }],
      dragOverTimeout: null,
      previousDragOverId: null
    }
  },
  computed: {
    /**
     * Get the item key.
     */
    itemKey () {
      // Get key from tree configuration.
      return this.tree?.key || 'id'
    },

    /**
     * Get the item text.
     */
    itemText () {
      // Get text from tree configuration.
      return !(this.tree?.text instanceof Function)
        ? this.tree?.text
        : undefined
    },

    /**
     * Get the item children.
     */
    itemChildren () {
      // Get children from tree configuration.
      return this.tree?.children
    },

    /**
     * Get filtered items.
     */
    filteredItems () {
      // Call filter function.
      return this.filter instanceof Function
        ? this.filter(this.editableTree)
        : this.editableTree?.items
    },

    /**
     * Get all opened items.
     */
    openedItems: {
      get () {
        // Check if search is empty.
        if ((this.search === '' && !this.openedId) || this.isExpanded) {
          // Return array of opened ids.
          return this.openedIds
        }

        // Return all open ids.
        return this.getAllOpenIds(this.filteredItems)
      },
      set (openedItems) {
        // Save opened ids.
        this.openedIds = openedItems.map((openedItem) => ({
          [this.itemKey]: openedItem?.[this.itemKey]
        }))
      }
    },

    editableItems () {
      // Return all open ids.
      return this.getAllEditableItems(this.filteredItems)
    },
    /**
     * Return item status code.
     */
    itemStatusCode () {
      return this.item.statusCode
    }
  },
  watch: {
    /**
     * Watch tree.
     */
    tree: {
      immediate: true,
      deep: true,
      handler (tree) {
        // Set editable tree.
        this.editableTree = tree
        // Wait for next tick.
        this.$nextTick(() => {
          // Set is expanded.
          this.isExpanded = !!tree?.autoExpand
        })
      }
    },

    /**
     * Watch search.
     */
    search: {
      immediate: false,
      handler () {
        // Reset opened id.
        this.openedId = null
      }
    },

    /**
     * Watch expand.
     */
    expand: {
      immediate: false,
      handler (expand) {
        // Set is expanded.
        this.isExpanded = !!expand
      }
    },

    /**
     * Watch isExpanded.
     */
    isExpanded: {
      immediate: false,
      handler (isExpanded) {
        this.$emit('update:expand', isExpanded)
        // If expand is true then mark all items as open.
        this.openedItems = isExpanded === true
          ? this.getAllOpenIds(this.filteredItems)
          : []
      }
    },

    /**
     * Watch open id.
     */
    openId: {
      immediate: true,
      handler (openId) {
        // Set default opened ids.
        this.openedIds = [{ [this.itemKey]: openId }]
      }
    },

    /**
     * Watch active item.
     */
    activeItem: {
      immediate: true,
      handler (activeItem, oldActiveItem) {
        // Check if key has not changed.
        if (
          JSON.stringify(activeItem) === JSON.stringify(oldActiveItem) ||
          JSON.stringify(activeItem) === JSON.stringify(this.activeItems?.[0])
        ) {
          // Stop execution.
          return
        }

        // Get target item tree and index.
        const { item } = this.findMatchingItem({
          item: activeItem,
          handler (item, comparingItem) {
            // Define is matching.
            let isMatching = true
            // Define has keys.
            let hasKeys = false
            // Loop all entries of item.
            for (const [key, value] of Object.entries(item)) {
              // Set has keys to true.
              hasKeys = true
              // Compare current value with comparing item.
              if (comparingItem?.[key] === undefined || JSON.stringify(comparingItem?.[key]) !== JSON.stringify(value)) {
                // Set is matching to false.
                isMatching = false
              }
            }

            // Return info if item is matching.
            return hasKeys && isMatching
          }
        })

        this.setActiveItem(item)
      }
    },

    /**
     * Watch active items.
     */
    activeItems: {
      deep: true,
      handler: function (activeItems, oldActiveItems) {
        // Check if key has not changed.
        if (activeItems?.[0]?.[this.itemKey] === oldActiveItems?.[0]?.[this.itemKey]) {
          // Stop execution.
          return
        }

        // Check to prevent double emits when activeItem is changed from outside this component
        if (activeItems?.[0]?.[this.itemKey] && (activeItems?.[0]?.[this.itemKey] === this.activeItem?.[this.itemKey])) {
          // Stop execution.
          return
        }

        // Check if editable mode is active.
        if (this.editable === true) {
          // Reset active items.
          this.activeItems = []
        }

        // Get current active item in base of tree key.
        const activeItem = activeItems.length > 0
          ? activeItems?.[0]
          : null

        // Check if active item is not null.
        if (activeItem !== null) {
          // Emit active item.
          this.emitActiveItem(activeItem)
        }
      }
    }
  },

  updated () {
    // Helper for get real text width of text element.
    const getRealTextWidth = (text) => {
      const c = document.createElement('canvas')
      const ctx = c.getContext('2d')
      ctx.font = '14px Roboto'
      return ctx.measureText(text).width + 10
    }

    // Wait for next tick.
    this.$nextTick(() => {
      // Clear existing timeout.
      window.clearTimeout(this.timeoutHandler)
      // Wait a second.
      this.timeoutHandler = window.setTimeout(() => {
        // Reset tooltip ids.
        this.tooltipIds = {}
        // Get all activators.
        const activators = document.querySelectorAll('[data-activator]')
        // Loop all activators.
        for (const activator of Array.from(activators)) {
          // Get client width of activator element.
          const clientWidth = activator.getBoundingClientRect()?.width || activator.clientWidth
          // Check if text is larger than available space.
          const textIsLargerThanAvailableSpace = getRealTextWidth(activator.innerText) > clientWidth
          // Get id of activator.
          const id = activator.getAttribute('data-activator')
          // Set information if text is larger than available space to tooltip ids.
          this.$set(this.tooltipIds, id, textIsLargerThanAvailableSpace)
        }
      }, 1000)
    })
  },

  methods: {
    /**
     * Check if an item is editable.
     */
    itemHasInsertOption ({ tree, item }) {
      const itemId = item?.[this.itemKey]
      const insertable = isFunction(tree.insertable) ? tree.insertable(item) : !!tree.insertable
      return !!(insertable && this.editableItems?.[itemId]?.isEditable)
    },

    /**
     * Check if an item is dragged.
     */
    isDragged (item) {
      // Return information if item is dragged.
      return this.draggedItem && item?.id && item?.id === this.draggedItem?.id
    },

    /**
     * Check if an item is editable.
     */
    isMovable (item) {
      // Get item id.
      const itemId = item?.[this.itemKey]
      // Return is movable state.
      return this.isUserLoggedIn === true &&
        this.editableItems?.[itemId]?.isEditable &&
        this.tree?.editable === true &&
        this.editable === true &&
        !!itemId
    },

    /**
     * Get index of item based on level and index.
     * @param item
     * @returns {string}
     */
    getIndex (item) {
      // Get item info via list helper.
      const itemInfo = this.$filters.listHelper.listHelper({
        items: this.filteredItems,
        children: this.itemChildren,
        handler: (currentItem) => currentItem === item
      })

      // Return index.
      return `index-${this.identifier}-${itemInfo?.level}-${itemInfo?.index}`
    },

    /**
     * Get all open ids.
     */
    getAllOpenIds (items) {
      // Define ids.
      const ids = []

      // Check if items are not a array.
      if (Array.isArray(items) === false) {
        // Return empty array.
        return []
      }

      // Check if opened id is not nil.
      if (this.openedId) {
        // Get editable items.
        const editableItems = this.getAllEditableItems(items)
        // Get all parent ids.
        const parentIds = editableItems?.[this.openedId]?.parentIds || []
        // Set opened id to null.
        this.openedId = null
        // Return empty array.
        return parentIds.map((parentId) => ({ [this.itemKey]: parentId }))
      }

      // Loop each item.
      for (const item of items) {
        // Get all children ids.
        ids.push(...this.getAllOpenIds(item?.[this.itemChildren]))
        // Push item key.
        ids.push({ [this.itemKey]: item[this.itemKey] })
      }

      // Return ids.
      return ids
    },

    /**
     * Get all editable items.
     */
    getAllEditableItems (items, parent, parentIds) {
      // Define editable items.
      const editableItems = []
      // Get prepared parent ids.
      const preparedParentIds = Array.isArray(parentIds)
        ? parentIds
        : []

      // Check if items are not a array.
      if (Array.isArray(items) === false) {
        // Return empty array.
        return {}
      }

      // Loop each item.
      for (const item of items) {
        // Check if item is editable.
        const isEditable = !parent?.inherit
        // Check if item is empty group.
        const isEmptyGroup = item?.type === 'group' && !item?.[this.itemChildren]?.length
        // Get all editable children items.
        editableItems.push(...Object.entries(this.getAllEditableItems(item?.[this.itemChildren], item, [...preparedParentIds, item?.[this.itemKey] || null])))
        // Push editable item.
        editableItems.push([item[this.itemKey], {
          parentId: parent?.[this.itemKey] || null,
          parentIds: preparedParentIds.filter((id) => id !== null),
          isEditable,
          isEmptyGroup
        }])
      }

      // Return editable items.
      return Object.fromEntries(editableItems)
    },

    /**
     * Set active item in current tree.
     */
    setActiveItem (activeItem) {
      // Set opened id.
      this.openedId = activeItem?.[this.itemKey] || this.openedId
      // Set active items.
      this.activeItems = activeItem ? [activeItem] : []
      // Emit active item.
      this.emitActiveItem(activeItem)
    },

    /**
     * Emit active item.
     */
    emitActiveItem (activeItem) {
      // Check if active item is not null.
      if (activeItem !== null) {
        // Try to get onSelect callback.
        const onSelect = this.tree?.onSelect || null
        // Check if handler is instance of function.
        if (onSelect instanceof Function) {
          // Call handler and inject active item.
          onSelect(activeItem)
        }

        // Emit active item as input event.
        this.$emit('update:active-item', activeItem)
      }
    },

    /**
     * Get translated label.
     *
     * @return string
     */
    getTranslatedLabel (item, returnObject) {
      // Check if text key is a function.
      if (this.tree?.text instanceof Function) {
        return this.tree.text(item, returnObject)
      }

      // Check if text i18n key is available.
      if (!this.tree?.textI18n) {
        // Return the item.
        return item?.[this.tree.text]
      }

      // Return matching translation.
      return this.$filters.getMatchingTranslation({
        value: item?.[this.tree?.text],
        selector: this.tree.textI18n,
        returnObject
      })
    },

    /**
     * On mouse down event.
     *
     * @param $event
     * @param item
     */
    onMouseDown ({ $event, item }) {
      // Check if tree is editable and editable mode is active and the item itself is editable.
      if (this.tree.editable === true && this.editable === true && this.editableItems?.[item?.[this.itemKey]]?.isEditable === true) {
        // Set dragged item.
        this.draggedItem = item
        // Get treeview node.
        const treeviewNode = $event.target.closest('.v-treeview-node')
        // Define draggable handler.
        const draggableHandler = (event) => {
          // Set data transfer.
          event.dataTransfer.setData('text/plain', JSON.stringify(item))
          // Set draggable on treeview node back to false.
          treeviewNode.setAttribute('draggable', 'false')
          // Remove event listener.
          treeviewNode.removeEventListener('dragstart', draggableHandler)
        }

        // Make tree view node draggable.
        treeviewNode.setAttribute('draggable', 'true')
        // Make tree view node non draggable.
        treeviewNode.addEventListener('dragstart', draggableHandler)
      }
    },

    /**
     * On drag over event.
     *
     * @param $event
     * @param item
     * @param position
     */
    onDragOver ({ $event, item, position }) {
      // Prevent default.
      $event.preventDefault()
      // Emit on drag over event.
      this.$emit('onDragOver')
      // Expand item if it has children and is not already opened.
      if (
        this.previousDragOverId !== item?.[this.itemKey] &&
        Array.isArray(item?.[this.itemChildren]) &&
        item[this.itemChildren].length &&
        !this.openedItems.some((openItem) => openItem[this.itemKey] === item[this.itemKey])
      ) {
        if (this.dragOverTimeout) clearTimeout(this.dragOverTimeout)

        this.dragOverTimeout = setTimeout(() => {
          this.openedItems = this.openedItems.concat([{ [this.itemKey]: item[this.itemKey] }])
        }, 800)
      } else if (this.previousDragOverId !== item?.[this.itemKey] && this.dragOverTimeout !== null) {
        clearTimeout(this.dragOverTimeout)
      }

      // Check if editable item is allowed to drag over and current item will not moved to itself.
      if (
        !this.tree?.notDroppable &&
        (position !== 'into' || item?.id !== this.draggedItem?.id) &&
        this.editableItems?.[item?.[this.itemKey]]?.isEditable === true &&
        this.editableItems?.[item?.[this.itemKey]]?.parentIds.includes(this.draggedItem?.[this.itemKey]) === false
      ) {
        // Add drag over class.
        $event.target.classList.add('drag-over')
      }

      this.previousDragOverId = item?.[this.itemKey]
    },

    /**
     * On drag leave event.
     *
     * @param $event
     */
    onDragLeave ({ $event }) {
      // Prevent default.
      $event.preventDefault()
      // Remove drag over class.
      $event.target.classList.remove('drag-over')
      // Emit on drag leave event.
      this.$emit('onDragLeave')
    },

    /**
     * On drop event.
     *
     * @param $event
     * @param item
     * @param position
     */
    onDrop ({ $event, item, position }) {
      // Prevent default.
      $event.preventDefault()
      // Remove drag over class.
      $event.target.classList.remove('drag-over')
      // Emmit on drop event.
      this.$emit('onDrop')

      // Check if editable item is allowed to drag over and current item will not moved to itself.
      if (
        !this.tree?.notDroppable &&
        (position !== 'into' || item?.id !== this.draggedItem?.id) &&
        this.editableItems?.[item?.[this.itemKey]]?.isEditable === true &&
        this.editableItems?.[item?.[this.itemKey]]?.parentIds.includes(this.draggedItem?.[this.itemKey]) === false
      ) {
        // Move item in tree.
        this.moveItemInTree({ item, position })

        // Emit input changes on tree.
        this.$emit('update:tree', this.editableTree)
      }
    },

    /**
     * Check if item is empty group.
     */
    isEmptyGroup ({ item }) {
      // Return state.
      return this.editableItems?.[item?.[this.itemKey]]?.isEmptyGroup === true
    },

    /**
     * Get matching item tree with index.
     *
     * @param items
     * @param item
     * @param parent
     * @param ignore
     * @param handler
     * @return {{items, key: string}|*|null}
     */
    findMatchingItem ({ items, item, parent, ignore, handler }) {
      // Get parsed items.
      const parsedItems = items || this.editableTree?.items || []
      // Loop all items.
      for (const [index, currentItem] of Object.entries(parsedItems)) {
        // Check if id or item through handler is matching.
        if (
          (!(handler instanceof Function) && currentItem?.[this.itemKey] === item?.[this.itemKey]) ||
          (!!item === true && handler instanceof Function && handler(item, currentItem) === true)
        ) {
          // Return items, item, index and parent.
          return {
            status: true,
            items: parsedItems,
            item: currentItem,
            index,
            parent: parent
              ? { ...parent, [this.itemChildren]: null }
              : null
          }
        } else if (
          !!currentItem?.[this.itemChildren] === true &&
          currentItem?.[this.itemKey] !== ignore?.[this.itemKey]
        ) {
          // Find matching item on children.
          const childrenResponse = this.findMatchingItem({
            items: currentItem?.[this.itemChildren],
            item,
            parent: currentItem,
            ignore,
            handler
          })

          // Check if children response is not nil.
          if (!!childrenResponse.status === true) {
            // Return children response.
            return childrenResponse
          }
        }
      }

      // Return empty object.
      return {
        status: false,
        items: [],
        item: null,
        index: null,
        parent: null
      }
    },
    /**
     * Move item in tree.
     *
     * @param items
     * @param item
     * @param position
     */
    moveItemInTree ({ item, position }) {
      // Get original item tree and index.
      const original = this.findMatchingItem({ item: this.draggedItem })
      // Get target item tree and index.
      const target = this.findMatchingItem({ item, ignore: this.draggedItem })

      // Check if original item tree and index is not nil.
      if (!!original === true && !!target === true) {
        // Check if target and original are on same scope.
        const sameScope = target.items === original.items
        // Check if movement is ascending.
        const ascending = parseInt(original.index) < parseInt(target.index)
        // Define target index.
        let targetIndex = parseInt(target.index)

        // Check if same scope.
        if (sameScope === true) {
          // Check if position is before.
          if (position === 'before') {
            // Decrement target index.
            targetIndex--
          }

          // Check if sort is not ascending.
          if (ascending === false) {
            // Decrement target index.
            targetIndex++
          }
        }

        // Check if not same scope.
        if (sameScope === false) {
          // Check if position is after.
          if (position === 'after') {
            // Increment target index.
            targetIndex++
          }
        }

        // Mark dragged item as moved.
        this.draggedItem.move = [{ move: true }]

        // Remove original item,
        original.items.splice(original.index, 1)
        // Check if position is not into.
        if (position !== 'into') {
          // Move dragged item to target item.
          target.items.splice(targetIndex, 0, this.draggedItem)
        } else {
          // Check if target item has no children.
          if (Array.isArray(target.item?.[this.itemChildren]) === false) {
            // Set children array.
            this.$set(target.item, this.itemChildren, [])
          }

          // Move dragged item into target item.
          target.item[this.itemChildren].push(this.draggedItem)
        }
      }
    },
    /**
     * On insert.
     *
     * @param position
     */
    onInsert (position) {
      // Check if tree has on insert function.
      if (this.tree?.onInsert instanceof Function) {
        // Get target item tree and index.
        const target = this.findMatchingItem({ item: position.item })
        // Call on insert method.
        this.tree.onInsert({ ...position, target })
      }
    },

    /**
     * return true if tree has no data
     */
    treeHasNoData () {
      return (t) => {
        return this.indicateNoData ? t?.items?.length === 0 : false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin treeviewLevelHelper($depth, $level: 0) {
  + .v-treeview-node__children > .v-treeview-node > .v-treeview-node__root {
    div:nth-last-child(#{$level + 3}) {
      visibility: hidden;
    }

    @if ($level < $depth) {
      @include treeviewLevelHelper($depth, $level + 1)
    }
  }
}

.treeview {
  ::v-deep {
    .drag-over {
      position: relative;

      &.dropable-layer-before:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 50%;
        border-top: 2px solid rgba(255, 0, 0, 0.5);
        background: rgba(255, 0, 0, 0.25);
        z-index: 5;
      }

      &.dropable-layer-after:before {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 50%;
        border-bottom: 2px solid rgba(255, 0, 0, 0.5);
        background: rgba(255, 0, 0, 0.25);
        z-index: 5;
      }

      &.dropable-layer-into:before {
        content: '';
        position: absolute;
        left: 0;
        top: -25%;
        width: 100%;
        height: 150%;
        background: rgba(255, 0, 0, 0.5);
        z-index: 10;
      }
    }

    .v-treeview {
      padding: 0 0 0 16px;
      margin: 0 0 0 -16px;

      .v-treeview-node__label .dropable-layer {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      .treeview-node-actions {
        display: none;
      }

      .v-treeview-node--active {
        .treeview-node-actions {
          position: relative;
          width: 20px;
          height: 20px;
          display: block;

          .v-btn {
            position: absolute;
            right: 0;
            top: 1px;
          }
        }
      }
    }

    .is-movable {
      cursor: move;
    }

    .dropable-layer {
      .dropable-layer-before {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 50%;
      }

      .dropable-layer-after {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 50%;
      }

      .dropable-layer-into {
        position: absolute;
        left: 0;
        top: 25%;
        width: 100%;
        height: 50%;
      }
    }

    .v-treeview-node {
      position: relative;
      cursor: pointer;

      &--disabled {
        cursor: not-allowed;
      }

      .v-treeview-node__root {
        min-height: 27px;
        align-items: stretch;
      }

      .v-treeview-node__toggle {
        top: 1px;
        width: 24px;
        height: 24px;

        background-color: #ffffff;
        border-radius: 100%;
        z-index: 1;

        &:after {
          transform: scale(1);
        }
      }

      .v-treeview-node__content {
        margin-left: 0;
      }

      .v-treeview-node__level {
        position: relative;

        &:before {
          content: '';
          position: absolute;
          left: 11px;
          top: 0;
          height: 100%;
          border-left: 1px dotted #aaaaaa;
        }
      }

      .v-treeview-node__content {
        position: relative;

        &:before {
          content: '';
          position: absolute;
          left: -12px;
          top: 13px;
          width: 10px;
          border-top: 1px dotted #aaaaaa;
        }
      }

      &:last-child {
        > .v-treeview-node__root {
          div:nth-last-child(2) {
            &.v-treeview-node__level {
              &:before {
                height: 50%;
              }
            }
          }

          @include treeviewLevelHelper(20)
        }
      }
    }
  }
}
</style>
